// @oasis-permission-inherits: /users (users:view)
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { LookupFormUI } from "~/modules/lookup/components/lookup-form-ui";
import { RecentSearches } from "~/modules/lookup/components/recent-searches";
import { useSavedSearches } from "~/modules/lookup/hooks/use-saved-searches";
import { userSearchQuery } from "~/modules/lookup/queries";

export const Route = createFileRoute("/_dash/users/")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const { getSearchesByType } = useSavedSearches();

  const userSearches = getSearchesByType("user");

  // Search query - only enabled when we have a debounced search value
  const { data, isFetching } = userSearchQuery.useQuery(
    {
      params: { query: debouncedSearchValue },
    },
    {
      enabled: debouncedSearchValue.length > 0,
      placeholderData: (previousData) => previousData,
    }
  );

  // Debounced callback for search
  const debouncedSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearchValue(value);
  }, 300);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (value.trim()) {
      debouncedSearch(value.trim());
    } else {
      setDebouncedSearchValue("");
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchValue.trim();
    if (!trimmed) {
      return;
    }
    // If the input matches a known user ID format (alphanumeric or Apple-style), navigate directly
    if (/^([a-zA-Z0-9_-]+|\d+\.[a-f0-9]+\.\d+)$/.test(trimmed)) {
      navigate({ to: "/users/view/$userId", params: { userId: trimmed } });
    } else {
      setDebouncedSearchValue(trimmed);
    }
  };

  const handleClear = () => {
    setSearchValue("");
    setDebouncedSearchValue("");
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").trim();
    if (pastedText && /^([a-zA-Z0-9_-]+|\d+\.[a-f0-9]+\.\d+)$/.test(pastedText)) {
      navigate({ to: "/users/view/$userId", params: { userId: pastedText } });
    } else if (pastedText) {
      handleSearchChange(pastedText);
    }
  };

  const handleUserClick = (userId: string) => {
    navigate({ to: "/users/view/$userId", params: { userId } });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">Users</h1>
      </div>

      <LookupFormUI
        placeholder="Enter user ID or search by name/email..."
        searchValue={searchValue}
        isPending={false}
        hasActiveSearch={searchValue.length > 0}
        onSearchValueChange={handleSearchChange}
        onSubmit={handleSearch}
        onClear={handleClear}
        onPaste={handlePaste}
      />

      {/* Search Results */}
      {debouncedSearchValue && (
        <div className="space-y-4">
          <h3 className="font-medium">Search Results</h3>
          <div className="space-y-2">
            {isFetching && !data && (
              <div className="rounded-md border p-3 text-center text-muted-foreground">
                Searching...
              </div>
            )}
            {!(isFetching && !data) &&
              data?.users &&
              data.users.length > 0 &&
              data.users.map((user) => (
                <button
                  key={user.id}
                  className="flex w-full items-center justify-between rounded-md border p-3 text-left hover:bg-accent"
                  onClick={() => handleUserClick(user.id)}
                >
                  <div>
                    <div className="font-medium">{user.name || "No name"}</div>
                    <div className="text-sm text-muted-foreground">{user.email || "No email"}</div>
                  </div>
                  <span className="font-mono text-sm text-muted-foreground">{user.id}</span>
                </button>
              ))}
            {!(isFetching && !data) && !(data?.users && data.users.length > 0) && (
              <div className="rounded-md border p-3 text-center text-muted-foreground">
                No users found matching &quot;{debouncedSearchValue}&quot;
              </div>
            )}
          </div>
        </div>
      )}

      <RecentSearches searches={userSearches} type="user" />
    </div>
  );
}
